import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { List } from 'immutable';
import { deselectLabels } from '../../../../common/labels/labelActions';
import { Label } from '../../../../common/labels/labelTypes';
import useUpdatedRef from '../../../utils/react/useUpdatedRef';

type UseLabelSelectionArgs = {
    labels: List<Label>;
    selectedLabelId: string | undefined;
    isElementSingleSelected: boolean;
    isSelectedLabelInThisContainer: boolean;
};

/**
 * Clears label selection when this container no longer owns a valid selected label.
 */
export const useLabelSelection = ({
    labels,
    selectedLabelId,
    isElementSingleSelected,
    isSelectedLabelInThisContainer,
}: UseLabelSelectionArgs) => {
    const dispatch = useDispatch();

    // Keep unmount cleanup aligned with the latest label ownership state.
    const hasSelectedLabelInThisContainerRef = useUpdatedRef(isSelectedLabelInThisContainer);

    // Clear selection when the label no longer belongs to this single-selected element.
    useEffect(() => {
        if (!isSelectedLabelInThisContainer) return;
        if (isElementSingleSelected && labels.some((label) => label?._id === selectedLabelId)) return;

        dispatch(deselectLabels());
    }, [dispatch, isElementSingleSelected, isSelectedLabelInThisContainer, labels, selectedLabelId]);

    // Clear selection if this owning label container unmounts.
    useEffect(() => {
        return () => {
            if (!hasSelectedLabelInThisContainerRef.current) return;

            dispatch(deselectLabels());
        };
    }, [dispatch]);
};
